import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { getAppSocket } from '../lib/wesSocket';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../components/ui/breadcrumb';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PERMISSION_OPTIONS, ROLE_PERMISSION_PRESETS, SKILL_OPTIONS } from '../constants/userOptions';
import { Tip } from '../components/ui/tip';

interface SkillOption {
  value: string;
  label: string;
  isActive?: boolean;
}

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  skills?: string[];
  permissions?: string[];
  inactiveReason?: string | null;
  inactiveNote?: string | null;
  operatorPin?: string;
}

interface UserForm {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
  operatorPin?: string;
  role: string;
  skills?: string[];
  isActive?: boolean;
  inactiveReason?: string | null;
  inactiveNote?: string | null;
  permissions?: string[];
}

const inactiveReasons = [
  { value: 'vacaciones', label: 'Vacaciones' },
  { value: 'enfermedad', label: 'Enfermedad' },
  { value: 'licencia', label: 'Licencia' },
  { value: 'suspendido', label: 'Suspendido' },
  { value: 'otro', label: 'Otro' }
];

const normalizeStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((item) => typeof item === 'string');
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
    } catch {
      return [];
    }
  }
  return [];
};

const ALL_PERMISSION_IDS = PERMISSION_OPTIONS.map((perm) => perm.id);

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrador',
  mecanico: 'Mecanico',
  cajero: 'Cajero',
  recepcionista: 'Recepcionista',
  operario: 'Operario'
};
const roleDisplayLabel = (name: string): string =>
  ROLE_LABELS[name] || (name ? name.charAt(0).toUpperCase() + name.slice(1) : '');

const uniqueStrings = (items: string[]): string[] => Array.from(new Set(items));

// Permisos base de un rol: los de la DB; '*' = todos. Fallback a presets locales.
const expandBasePermissions = (perms: string[] | undefined, fallbackRole: string): string[] => {
  const source = perms && perms.length > 0 ? perms : (ROLE_PERMISSION_PRESETS[fallbackRole] || []);
  if (source.includes('*')) return [...ALL_PERMISSION_IDS];
  return uniqueStrings(source);
};

// Aplica el delta { added, removed } del usuario sobre la base del rol.
const applyDeltaToBase = (base: string[], rawPermissions: unknown): string[] => {
  if (rawPermissions && typeof rawPermissions === 'object' && !Array.isArray(rawPermissions)) {
    const obj = rawPermissions as { added?: unknown; removed?: unknown };
    if (Array.isArray(obj.added) || Array.isArray(obj.removed)) {
      const added = normalizeStringArray(obj.added);
      const removed = new Set(normalizeStringArray(obj.removed));
      return uniqueStrings([...base, ...added]).filter((p) => !removed.has(p));
    }
    // Forma legacy { perm: true } -> lista completa
    const legacy = Object.entries(rawPermissions as Record<string, unknown>)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([perm]) => perm);
    return uniqueStrings(legacy);
  }
  // Lista legacy completa
  const legacyList = normalizeStringArray(rawPermissions);
  if (legacyList.length > 0) return legacyList;
  return [...base];
};

const UserEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isCreate = !id;
  const { register, handleSubmit, reset, watch, formState: { errors, isDirty } } = useForm<UserForm>();
  const [loading, setLoading] = useState(!isCreate);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [roleBaseMap, setRoleBaseMap] = useState<Record<string, string[]>>({});
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [editingPermissions, setEditingPermissions] = useState(false);
  const [skillOptions, setSkillOptions] = useState<SkillOption[]>(SKILL_OPTIONS);
  const [skillPickerOpen, setSkillPickerOpen] = useState(false);
  const [skillQuery, setSkillQuery] = useState('');
  const [roleList, setRoleList] = useState<string[]>([]);
  const [supervisorScope, setSupervisorScope] = useState<{ type: 'all' | 'area' | 'users'; areaIds: string[]; userIds: string[] }>({ type: 'all', areaIds: [], userIds: [] });
  const [areasList, setAreasList] = useState<{ id: string; name: string }[]>([]);
  const [usersList, setUsersList] = useState<{ id: string; name: string }[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [inactiveReason, setInactiveReason] = useState('');
  const [inactiveNote, setInactiveNote] = useState('');
  const watchedRole = watch('role');
  const rawPermissionsRef = useRef<unknown>(null);
  const hydratedRef = useRef(false);
  const prevRoleRef = useRef<string>('');

  const safeSelectedSkills = normalizeStringArray(selectedSkills);
  const safeSelectedPermissions = normalizeStringArray(selectedPermissions);

  const fetchUser = useCallback(async (options?: { silent?: boolean }) => {
    if (!id) return;
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    try {
      const response = await axios.get(`/api/users/${id}`);
      const user: User = response.data.user;
      reset({
        username: user.username,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        password: '',
        operatorPin: (user as any).operatorPin || ''
      });
      setSelectedSkills(normalizeStringArray(user.skills));
      const sc = (user as any).supervisorScope;
      if (sc && typeof sc === 'object') {
        setSupervisorScope({
          type: sc.type === 'area' || sc.type === 'users' ? sc.type : 'all',
          areaIds: Array.isArray(sc.areaIds) ? sc.areaIds : [],
          userIds: Array.isArray(sc.userIds) ? sc.userIds : []
        });
      }
      rawPermissionsRef.current = user.permissions ?? null;
      setIsActive(Boolean(user.isActive));
      setInactiveReason(user.inactiveReason || '');
      setInactiveNote(user.inactiveNote || '');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al cargar el usuario');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id, reset]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const realtimeTimerRef = useRef<number | null>(null);
  const pendingRefreshRef = useRef(false);
  const notifiedRef = useRef(false);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (path && !(path.startsWith('/api/users') || path.startsWith('/api/roles'))) {
        return;
      }

      if (isDirty) {
        pendingRefreshRef.current = true;
        if (!notifiedRef.current) {
          notifiedRef.current = true;
          toast.message('Se detectaron cambios externos. Se actualizará al guardar o descartar cambios.');
        }
        return;
      }

      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchUser({ silent: true });
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [fetchUser, isDirty]);

  useEffect(() => {
    if (isDirty) return;
    if (!pendingRefreshRef.current) return;
    pendingRefreshRef.current = false;
    notifiedRef.current = false;
    fetchUser({ silent: true });
  }, [isDirty, fetchUser]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get('/api/roles');
        const map: Record<string, string[]> = {};
        const names: string[] = [];
        for (const role of response.data.roles || []) {
          map[role.name] = normalizeStringArray(role.permissions);
          names.push(role.name);
        }
        if (!cancelled) { setRoleBaseMap(map); setRoleList(names); }
      } catch {
        // Si falla, se usan los presets locales como fallback
      } finally {
        if (!cancelled) setRolesLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get('/api/settings/areas').catch(() => ({ data: { areas: [] } })),
      axios.get('/api/users').catch(() => ({ data: { users: [] } }))
    ]).then(([areasRes, usersRes]) => {
      if (cancelled) return;
      setAreasList((areasRes.data?.areas || []).map((a: any) => ({ id: a.id, name: a.name })));
      setUsersList((usersRes.data?.users || [])
        .filter((u: any) => u.id !== id && String(u.role || '').toLowerCase() !== 'admin')
        .map((u: any) => ({ id: u.id, name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username })));
    });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/skills/catalog')
      .then((res) => {
        const list = (res.data?.skills || []).map((s: any) => ({ value: s.value, label: s.label, isActive: s.isActive !== false }));
        if (!cancelled && list.length > 0) setSkillOptions(list);
      })
      .catch(() => { /* fallback a SKILL_OPTIONS */ });
    return () => { cancelled = true; };
  }, []);

  // Hidratacion inicial y reseteo al cambiar de rol.
  useEffect(() => {
    if (watchedRole === undefined || watchedRole === null) return; // form aun no listo
    if (!rolesLoaded) return; // esperar la base de roles
    const base = watchedRole ? expandBasePermissions(roleBaseMap[watchedRole], watchedRole) : [];

    if (!hydratedRef.current) {
      if (isCreate) {
        setSelectedPermissions(base);
      } else if (rawPermissionsRef.current !== null) {
        setSelectedPermissions(applyDeltaToBase(base, rawPermissionsRef.current));
      } else {
        return; // todavia no llegaron los datos del usuario
      }
      hydratedRef.current = true;
      prevRoleRef.current = watchedRole;
      return;
    }

    if (prevRoleRef.current !== watchedRole) {
      // Cambio de rol hecho por el usuario -> arrancar desde la base del nuevo rol
      setSelectedPermissions(base);
      prevRoleRef.current = watchedRole;
    }
  }, [watchedRole, rolesLoaded, roleBaseMap, isCreate]);

  const toggleSkill = (skill: string, checked: boolean) => {
    setSelectedSkills((prev) => {
      const normalized = normalizeStringArray(prev);
      if (checked) {
        return normalized.includes(skill) ? normalized : [...normalized, skill];
      }
      return normalized.filter((item) => item !== skill);
    });
  };

  const togglePermission = (permissionId: string, checked: boolean) => {
    setSelectedPermissions((prev) => {
      const normalized = normalizeStringArray(prev);
      if (checked) {
        return normalized.includes(permissionId) ? normalized : [...normalized, permissionId];
      }
      return normalized.filter((item) => item !== permissionId);
    });
  };

  const permissionsByCategory = useMemo(() => {
    return PERMISSION_OPTIONS.reduce((acc, perm) => {
      if (!acc[perm.category]) acc[perm.category] = [];
      acc[perm.category].push(perm);
      return acc;
    }, {} as Record<string, typeof PERMISSION_OPTIONS>);
  }, []);

  const permissionBaseSet = useMemo(
    () => new Set(expandBasePermissions(roleBaseMap[watchedRole], watchedRole)),
    [roleBaseMap, watchedRole]
  );

  const permissionDelta = useMemo(() => {
    const base = expandBasePermissions(roleBaseMap[watchedRole], watchedRole);
    const baseSet = new Set(base);
    const checkedSet = new Set(selectedPermissions);
    return {
      added: selectedPermissions.filter((p) => !baseSet.has(p)).length,
      removed: base.filter((p) => !checkedSet.has(p)).length
    };
  }, [roleBaseMap, watchedRole, selectedPermissions]);

  const onSubmit = async (data: UserForm) => {
    try {
      const { username, ...rest } = data;
      const payload: any = { ...rest };
      payload.skills = selectedSkills;
      const base = expandBasePermissions(roleBaseMap[data.role], data.role);
      const baseSet = new Set(base);
      const checkedSet = new Set(selectedPermissions);
      const added = selectedPermissions.filter((p) => !baseSet.has(p));
      const removed = base.filter((p) => !checkedSet.has(p));
      payload.permissions = { added, removed };
      payload.supervisorScope = selectedPermissions.includes('supervisor') ? supervisorScope : null;
      if (!payload.password || payload.password.trim() === '') {
        delete payload.password;
      }
      // PIN: si quedó vacío significa "sin PIN" (no permite acceso al monitor)
      if (payload.operatorPin === undefined) {
        delete payload.operatorPin;
      } else {
        payload.operatorPin = payload.operatorPin.trim() || null;
      }
      if (!isActive && !inactiveReason) {
        toast.error('Selecciona el motivo de inactividad');
        return;
      }
      if (!isActive && inactiveReason === 'otro' && !inactiveNote.trim()) {
        toast.error('Detalla el motivo');
        return;
      }
      payload.isActive = isActive;
      payload.inactiveReason = isActive ? null : inactiveReason;
      payload.inactiveNote = isActive ? null : inactiveNote.trim() || null;

      if (isCreate) {
        payload.username = username;
        await axios.post('/api/users', payload);
        toast.success('Usuario creado');
      } else {
        await axios.put(`/api/users/${id}`, payload);
        toast.success('Usuario actualizado');
      }
      navigate('/users');
    } catch (error: any) {
      toast.error(error.response?.data?.error || (isCreate ? 'Error al crear el usuario' : 'Error al actualizar el usuario'));
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <button onClick={() => navigate('/settings')} className="text-left">
                Configuración
              </button>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <button onClick={() => navigate('/users')} className="text-left">
                Usuarios
              </button>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{isCreate ? 'Nuevo usuario' : 'Editar usuario'}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">{isCreate ? 'Nuevo usuario' : 'Editar usuario'}</h1>
        <Button variant="outline" size="sm" onClick={() => navigate('/users')}>
          Volver
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          <form className="grid gap-4" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium">{isCreate ? 'Usuario *' : 'Usuario (solo lectura)'}</Label>
                {isCreate ? (
                  <Input {...register('username', { required: 'Usuario requerido' })} placeholder="Nombre de usuario" autoComplete="off" />
                ) : (
                  <Input {...register('username')} disabled />
                )}
                {isCreate
                  ? errors.username && <p className="text-xs text-destructive">{errors.username.message}</p>
                  : <p className="text-xs text-muted-foreground">El usuario no se puede modificar.</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Email</Label>
                <Input
                  {...register('email', {
                    validate: (value) => {
                      if (!value || value.trim() === '') return true;
                      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'Email inválido';
                    },
                  })}
                  placeholder="email@ejemplo.com (opcional)"
                />
                {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Nombre *</Label>
                <Input {...register('firstName', { required: 'Nombre requerido' })} placeholder="Nombre" />
                {errors.firstName && <p className="text-xs text-destructive">{errors.firstName.message}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Apellido *</Label>
                <Input {...register('lastName', { required: 'Apellido requerido' })} placeholder="Apellido" />
                {errors.lastName && <p className="text-xs text-destructive">{errors.lastName.message}</p>}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium">{isCreate ? 'Contraseña *' : 'Contraseña (opcional)'}</Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  {...register('password', {
                    required: isCreate ? 'Contrasena requerida' : false,
                    validate: (value) => {
                      if (value && value.length < 6) return 'Mínimo 6 caracteres';
                      return true;
                    },
                  })}
                  placeholder={isCreate ? 'Mínimo 6 caracteres' : 'Dejar vacio para no cambiar'}
                />
                {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">PIN del Taller (4-6 dígitos)</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    {...register('operatorPin', {
                      validate: (value) => {
                        if (!value) return true;
                        if (!/^\d{4,6}$/.test(value)) return 'Solo números, 4 a 6 dígitos';
                        return true;
                      },
                    })}
                    placeholder="Ej: 1234"
                    onChange={(e) => { e.target.value = e.target.value.replace(/\D/g, '').slice(0, 6); }}
                  />
                  <Tip label="Generar PIN aleatorio">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const random = String(Math.floor(1000 + Math.random() * 9000));
                      const event = new Event('input', { bubbles: true });
                      const input = document.querySelector('input[name="operatorPin"]') as HTMLInputElement;
                      if (input) {
                        const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
                        setter?.call(input, random);
                        input.dispatchEvent(event);
                      }
                    }}
                  >
                    🎲
                  </Button>
                  </Tip>
                </div>
                <p className="text-xs text-muted-foreground">Para que pueda iniciar/marcar tareas en el monitor del taller. Vacío = sin acceso al monitor.</p>
                {errors.operatorPin && <p className="text-xs text-destructive">{errors.operatorPin.message}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Rol</Label>
                <select
                  {...register('role')}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="">Sin rol</option>
                  {(roleList.length ? roleList : ['admin', 'mecanico', 'cajero', 'recepcionista', 'operario']).map((name) => (
                    <option key={name} value={name}>{roleDisplayLabel(name)}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">Opcional. Sin rol = solo los permisos que le agregues manualmente.</p>
                {errors.role && <p className="text-xs text-destructive">{errors.role.message}</p>}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Estado *</Label>
                <select
                  value={isActive ? 'active' : 'inactive'}
                  onChange={(e) => setIsActive(e.target.value === 'active')}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                </select>
              </div>
              {!isActive && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Motivo *</Label>
                  <select
                    value={inactiveReason}
                    onChange={(e) => setInactiveReason(e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Seleccionar...</option>
                    {inactiveReasons.map((reason) => (
                      <option key={reason.value} value={reason.value}>{reason.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {!isActive && inactiveReason === 'otro' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Detalle *</Label>
                <Input
                  value={inactiveNote}
                  onChange={(e) => setInactiveNote(e.target.value)}
                  placeholder="Especifica el motivo"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium">Skills</Label>
              {(() => {
                const selected = safeSelectedSkills;
                const available = skillOptions.filter((s) => s.isActive !== false && !selected.includes(s.value));
                const filtered = available.filter((s) => s.label.toLowerCase().includes(skillQuery.trim().toLowerCase()));
                const addSkill = (value: string) => { toggleSkill(value, true); setSkillPickerOpen(false); setSkillQuery(''); };
                return (
                  <>
                    <Popover open={skillPickerOpen} onOpenChange={(o) => { setSkillPickerOpen(o); if (!o) setSkillQuery(''); }}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between font-normal text-muted-foreground"
                        >
                          Agregar skill...
                          <ChevronUpDownIcon className="h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0" align="start" style={{ width: 'var(--radix-popover-trigger-width)' }}>
                        <div className="border-b p-2">
                          <Input
                            autoFocus
                            placeholder="Buscar skill..."
                            value={skillQuery}
                            onChange={(e) => setSkillQuery(e.target.value)}
                            className="h-8"
                          />
                        </div>
                        <div className="max-h-[300px] overflow-y-auto p-1">
                          {available.length === 0 ? (
                            <div className="px-2 py-3 text-center text-sm text-muted-foreground">No hay mas skills para agregar</div>
                          ) : filtered.length === 0 ? (
                            <div className="px-2 py-3 text-center text-sm text-muted-foreground">No se encontraron skills</div>
                          ) : (
                            filtered.map((s) => (
                              <button
                                key={s.value}
                                type="button"
                                onClick={() => addSkill(s.value)}
                                className="flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                              >
                                {s.label}
                              </button>
                            ))
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>
                    {selected.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {selected.map((value) => {
                          const opt = skillOptions.find((s) => s.value === value);
                          const label = opt?.label || value;
                          const inactive = opt ? opt.isActive === false : true;
                          return (
                            <span key={value} className="inline-flex items-center gap-1 rounded-full border bg-muted px-2.5 py-1 text-sm">
                              {label}{inactive ? ' (inactiva)' : ''}
                              <Tip label="Quitar skill">
                                <button
                                  type="button"
                                  onClick={() => toggleSkill(value, false)}
                                  className="text-muted-foreground hover:text-foreground"
                                >
                                  <XMarkIcon className="h-3.5 w-3.5" />
                                </button>
                              </Tip>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium">Permisos</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingPermissions(true)}
                >
                  Modificar
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Hereda los permisos del rol.
                {permissionDelta.added > 0 && ` +${permissionDelta.added} agregado(s).`}
                {permissionDelta.removed > 0 && ` -${permissionDelta.removed} quitado(s).`}
                {' '}Tocá "Modificar" para ajustarlos.
              </p>
            </div>

            <Dialog open={editingPermissions} onOpenChange={setEditingPermissions}>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Permisos</DialogTitle>
                  <DialogDescription>
                    Partís de los permisos del rol. Tildá para agregar o destildá para quitar.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex items-center justify-between gap-2 border-b pb-3">
                  <span className="text-xs text-muted-foreground">
                    <span className="text-green-600">Verde</span> = agregado · <span className="text-red-500">Rojo</span> = quitado
                  </span>
                  <Tip label="Dejar exactamente los permisos del rol (sin ajustes manuales)">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedPermissions(expandBasePermissions(roleBaseMap[watchedRole], watchedRole))}
                  >
                    Restablecer al rol
                  </Button>
                  </Tip>
                </div>
                <div className="space-y-3">
                  {Object.entries(permissionsByCategory).map(([category, perms]) => (
                    <div key={category} className="space-y-2">
                      <div className="text-xs font-semibold text-muted-foreground uppercase">{category}</div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {perms.map((perm) => {
                          const checked = safeSelectedPermissions.includes(perm.id);
                          const inBase = permissionBaseSet.has(perm.id);
                          const added = checked && !inBase;
                          const removed = !checked && inBase;
                          return (
                            <label key={perm.id} className="flex items-center gap-2 text-sm">
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(c) => togglePermission(perm.id, Boolean(c))}
                              />
                              <span className={added ? 'text-green-600 font-medium' : removed ? 'text-red-500 line-through' : ''}>
                                {perm.name}
                                {added && ' (+)'}
                                {removed && ' (−)'}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <DialogFooter>
                  <Button type="button" onClick={() => setEditingPermissions(false)}>
                    Listo
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {safeSelectedPermissions.includes('supervisor') && (
              <div className="space-y-3 rounded-md border p-4">
                <div>
                  <Label className="text-sm font-medium">Alcance de supervisión</Label>
                  <p className="text-xs text-muted-foreground">Qué eventos del personal (observaciones, acciones del taller, etc.) recibe este supervisor.</p>
                </div>
                <div className="flex flex-wrap gap-4">
                  {([['all', 'Todo el personal'], ['area', 'Por área'], ['users', 'Personas específicas']] as const).map(([val, lbl]) => (
                    <label key={val} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="supScope"
                        checked={supervisorScope.type === val}
                        onChange={() => setSupervisorScope((prev) => ({ ...prev, type: val }))}
                      />
                      <span>{lbl}</span>
                    </label>
                  ))}
                </div>
                {supervisorScope.type === 'area' && (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {areasList.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No hay áreas cargadas.</p>
                    ) : areasList.map((a) => (
                      <label key={a.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={supervisorScope.areaIds.includes(a.id)}
                          onCheckedChange={(c) => setSupervisorScope((prev) => ({
                            ...prev,
                            areaIds: c ? [...prev.areaIds, a.id] : prev.areaIds.filter((x) => x !== a.id)
                          }))}
                        />
                        <span>{a.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                {supervisorScope.type === 'users' && (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {usersList.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No hay usuarios.</p>
                    ) : usersList.map((u) => (
                      <label key={u.id} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={supervisorScope.userIds.includes(u.id)}
                          onCheckedChange={(c) => setSupervisorScope((prev) => ({
                            ...prev,
                            userIds: c ? [...prev.userIds, u.id] : prev.userIds.filter((x) => x !== u.id)
                          }))}
                        />
                        <span>{u.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => navigate('/users')}>Cancelar</Button>
              <Button type="submit" className="bg-black text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-gray-100">
                {isCreate ? 'Crear usuario' : 'Guardar cambios'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserEdit;
